// Colour parsing, conversion and WCAG contrast math shared by both portals.
// Understands hex (#rgb/#rgba/#rrggbb/#rrggbbaa), rgb()/rgba(), hsl()/hsla()
// and oklch() — the formats design tokens and section backgrounds accept — so
// pickers, previews and accessibility checks all agree on what a value means.

export interface RgbaColor {
  /** 0–255 */
  r: number;
  g: number;
  b: number;
  /** 0–1 */
  a: number;
}

const HEX_RE = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const RGB_FN_RE = /^rgba?\(([^)]*)\)$/i;
const HSL_FN_RE = /^hsla?\(([^)]*)\)$/i;
const OKLCH_FN_RE = /^oklch\(([^)]*)\)$/i;

/** The syntax family of a colour string; null when unrecognised. */
export function colorFamily(value: string): "hex" | "rgb" | "hsl" | "oklch" | null {
  const trimmed = value.trim();
  if (HEX_RE.test(trimmed)) return "hex";
  if (RGB_FN_RE.test(trimmed)) return "rgb";
  if (HSL_FN_RE.test(trimmed)) return "hsl";
  if (OKLCH_FN_RE.test(trimmed)) return "oklch";
  return null;
}

function parseHex(value: string): RgbaColor | null {
  const hex = value.slice(1);
  const size = hex.length <= 4 ? 1 : 2;
  const expand = (part: string) => parseInt(size === 1 ? part + part : part, 16);
  const channels: number[] = [];
  for (let i = 0; i < hex.length; i += size) {
    channels.push(expand(hex.slice(i, i + size)));
  }
  const [r, g, b, alpha] = channels;
  return { r, g, b, a: alpha === undefined ? 1 : alpha / 255 };
}

/** Parse one channel that may be a plain number or a percentage of `scale`. */
function channelNumber(token: string, scale: number): number | null {
  const match = /^(-?\d*\.?\d+)(%|deg)?$/.exec(token);
  if (!match) return token === "none" ? 0 : null;
  const value = Number(match[1]);
  return match[2] === "%" ? (value / 100) * scale : value;
}

/** Split a modern CSS colour function body into channels + optional alpha. */
function splitFunctionBody(body: string): { channels: string[]; alpha: string | null } {
  const [main, alphaPart] = body.split("/").map((part) => part.trim());
  const channels = main.split(/[\s,]+/).filter(Boolean);
  return { channels, alpha: alphaPart || null };
}

function parseAlpha(token: string | null): number | null {
  if (token === null) return 1;
  const value = channelNumber(token, 1);
  if (value === null) return null;
  return Math.min(1, Math.max(0, value));
}

function parseRgbFunction(body: string): RgbaColor | null {
  const { channels, alpha } = splitFunctionBody(body);
  // Legacy comma syntax carries alpha as a 4th channel.
  const alphaToken = alpha ?? (channels.length === 4 ? channels.pop() ?? null : null);
  if (channels.length !== 3) return null;
  const parts = channels.map((token) => channelNumber(token, 255));
  const a = parseAlpha(alphaToken);
  if (parts.some((part) => part === null) || a === null) return null;
  const clamp = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  const [r, g, b] = parts as number[];
  return { r: clamp(r), g: clamp(g), b: clamp(b), a };
}

function parseHslFunction(body: string): RgbaColor | null {
  const { channels, alpha } = splitFunctionBody(body);
  // Legacy comma syntax carries alpha as a 4th channel.
  const alphaToken = alpha ?? (channels.length === 4 ? channels.pop() ?? null : null);
  if (channels.length !== 3) return null;
  const h = channelNumber(channels[0], 1);
  const s = channelNumber(channels[1], 1);
  const l = channelNumber(channels[2], 1);
  const a = parseAlpha(alphaToken);
  if (h === null || s === null || l === null || a === null) return null;

  const sat = Math.min(1, Math.max(0, s));
  const light = Math.min(1, Math.max(0, l));
  const hue = ((h % 360) + 360) % 360;
  // CSS Color 4 HSL → sRGB.
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    return light - sat * Math.min(light, 1 - light) * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  const to255 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);
  return { r: to255(f(0)), g: to255(f(8)), b: to255(f(4)), a };
}

/** OKLab non-linearity → linear sRGB (matrices from the OKLab reference). */
function oklabToLinearSrgb(L: number, a: number, b: number): [number, number, number] {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
  ];
}

function linearToGamma(channel: number): number {
  const c = Math.min(1, Math.max(0, channel));
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

function parseOklchFunction(body: string): RgbaColor | null {
  const { channels, alpha } = splitFunctionBody(body);
  if (channels.length !== 3) return null;
  const L = channelNumber(channels[0], 1);
  // Chroma percentage is defined as 100% = 0.4.
  const C = channelNumber(channels[1], 0.4);
  const H = channelNumber(channels[2], 1);
  const a = parseAlpha(alpha);
  if (L === null || C === null || H === null || a === null) return null;

  const hueRad = (H * Math.PI) / 180;
  const [rLin, gLin, bLin] = oklabToLinearSrgb(L, C * Math.cos(hueRad), C * Math.sin(hueRad));
  return {
    r: Math.round(linearToGamma(rLin) * 255),
    g: Math.round(linearToGamma(gLin) * 255),
    b: Math.round(linearToGamma(bLin) * 255),
    a
  };
}

/** Parse a supported CSS colour string to sRGB; null when unrecognised. */
export function parseColor(value: string): RgbaColor | null {
  const trimmed = value.trim();
  if (HEX_RE.test(trimmed)) return parseHex(trimmed);
  const rgb = RGB_FN_RE.exec(trimmed);
  if (rgb) return parseRgbFunction(rgb[1]);
  const hsl = HSL_FN_RE.exec(trimmed);
  if (hsl) return parseHslFunction(hsl[1]);
  const oklch = OKLCH_FN_RE.exec(trimmed);
  if (oklch) return parseOklchFunction(oklch[1]);
  return null;
}

/** #rrggbb for the parsed colour (alpha dropped) — e.g. for <input type=color>. */
export function rgbaToHex({ r, g, b }: RgbaColor): string {
  const part = (v: number) => v.toString(16).padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** The alpha channel of a colour string (1 when opaque or unparseable). */
export function colorAlpha(value: string): number {
  return parseColor(value)?.a ?? 1;
}

/**
 * Re-emit a colour with the given alpha (0–1), preserving its syntax family:
 * hex → #rrggbb[aa], oklch → `oklch(… / a%)`, rgb → rgb()/rgba(). Returns the
 * input unchanged when it can't be parsed.
 */
export function withAlpha(value: string, alpha: number): string {
  const trimmed = value.trim();
  const clamped = Math.min(1, Math.max(0, alpha));
  const parsed = parseColor(trimmed);
  if (!parsed) return value;

  const oklch = OKLCH_FN_RE.exec(trimmed);
  if (oklch) {
    // Keep the author's L/C/H text untouched; only swap the alpha part.
    const body = oklch[1].split("/")[0].trim();
    return clamped >= 1 ? `oklch(${body})` : `oklch(${body} / ${Math.round(clamped * 100)}%)`;
  }
  if (HEX_RE.test(trimmed)) {
    const hex = rgbaToHex(parsed);
    if (clamped >= 1) return hex;
    return `${hex}${Math.round(clamped * 255).toString(16).padStart(2, "0")}`;
  }
  const { r, g, b } = parsed;
  return clamped >= 1
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${Math.round(clamped * 100) / 100})`;
}

/** Composite `top` over an opaque `bottom` (alpha blending). */
function composite(top: RgbaColor, bottom: RgbaColor): RgbaColor {
  if (top.a >= 1) return top;
  const blend = (t: number, b: number) => Math.round(t * top.a + b * (1 - top.a));
  return { r: blend(top.r, bottom.r), g: blend(top.g, bottom.g), b: blend(top.b, bottom.b), a: 1 };
}

/** WCAG relative luminance of an (opaque) sRGB colour. */
function relativeLuminance({ r, g, b }: RgbaColor): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

const WHITE: RgbaColor = { r: 255, g: 255, b: 255, a: 1 };

export interface WcagLevel {
  id: string;
  label: string;
  minRatio: number;
}

/** The WCAG 2.x contrast thresholds the checker reports against. */
export const WCAG_LEVELS: WcagLevel[] = [
  { id: "aa-normal", label: "AA — normal text", minRatio: 4.5 },
  { id: "aa-large", label: "AA — large text", minRatio: 3 },
  { id: "aaa-normal", label: "AAA — normal text", minRatio: 7 },
  { id: "aaa-large", label: "AAA — large text", minRatio: 4.5 }
];

export interface ContrastResult {
  /** e.g. 4.53 (rounded to 2dp) */
  ratio: number;
  levels: Array<WcagLevel & { pass: boolean }>;
}

/**
 * WCAG contrast ratio between two colour strings (unrounded). Translucent
 * backgrounds composite over white, then the foreground over that. Returns
 * null when either colour can't be parsed.
 */
export function contrastRatio(foreground: string, background: string): number | null {
  const fg = parseColor(foreground);
  const bg = parseColor(background);
  if (!fg || !bg) return null;

  const solidBg = composite(bg, WHITE);
  const solidFg = composite(fg, solidBg);
  const lighter = Math.max(relativeLuminance(solidFg), relativeLuminance(solidBg));
  const darker = Math.min(relativeLuminance(solidFg), relativeLuminance(solidBg));
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * WCAG contrast between a foreground and background colour string, reported
 * against every WCAG level. Returns null when either colour can't be parsed.
 */
export function evaluateContrast(foreground: string, background: string): ContrastResult | null {
  const ratio = contrastRatio(foreground, background);
  if (ratio === null) return null;
  return {
    ratio: Math.round(ratio * 100) / 100,
    levels: WCAG_LEVELS.map((level) => ({ ...level, pass: ratio >= level.minRatio }))
  };
}
